/**
 * payoutLimits.ts — Minimum payout thresholds per currency.
 *
 * Frontend mirror of supabase/functions/_shared/payoutLimits.ts, which cannot be
 * imported from here without breaking the Edge Function deploy bundle. The two
 * copies are held in sync by `src/__tests__/unit/edge/payoutLimitsMirror.test.ts`,
 * which fails if the constants or the grouping behaviour diverge — that test is
 * the sync mechanism, not a "keep in sync" request.
 *
 * All values are in the currency's smallest unit (cents, centavos, etc.).
 * Approximately equivalent to $50 USD.
 */

export const MINIMUM_PAYOUT_CENTS_BY_CURRENCY: Record<string, number> = {
  USD: 5_000,       // $50.00
  BRL: 25_000,      // R$250.00  (~$50 at R$5/USD)
};

/** Fallback for currencies not explicitly listed. Defaults to the USD threshold. */
export const DEFAULT_MINIMUM_PAYOUT_CENTS =
  MINIMUM_PAYOUT_CENTS_BY_CURRENCY["USD"];

/**
 * Returns the minimum payout threshold (in smallest currency unit) for the
 * given ISO 4217 currency code. Case-insensitive.
 * Falls back to $50 USD for unmapped currencies.
 *
 * @example
 *   getMinimumPayoutCents("BRL") // 25_000 (R$250)
 *   getMinimumPayoutCents("usd") // 5_000  ($50)
 *   getMinimumPayoutCents("XYZ") // 5_000  (fallback)
 */
export function getMinimumPayoutCents(currency: string): number {
  return (
    MINIMUM_PAYOUT_CENTS_BY_CURRENCY[currency.toUpperCase()] ??
    DEFAULT_MINIMUM_PAYOUT_CENTS
  );
}

// ─── Age backstop ─────────────────────────────────────────────────────────────

/**
 * Maximum age, in days, that a below-minimum balance may sit unpaid before it
 * is released regardless of the minimum.
 *
 * Why 83 and not 90: the cron runs hourly but only releases inside the Friday
 * UTC window, so a balance that crosses the backstop on a Saturday waits up to
 * 6 more days for the next Friday. 83 + 6 = 89 ≤ 90, which keeps the "paid
 * within 90 days" guarantee true. A literal 90 would fire at up to 96 days and
 * put the onboarding copy back to promising something the system does not do —
 * the very defect this change exists to remove.
 */
export const PAYOUT_AGE_BACKSTOP_DAYS = 83;

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/** Why a group was released. Distinguishes the two paths for the payout log. */
export type PayoutReleaseReason = "minimum_reached" | "age_backstop";

// ─── Release grouping ─────────────────────────────────────────────────────────

export interface PaymentForPayout {
  id: string;
  payee_id: string;
  currency: string;
  net_to_instructor_cents: number;
  /**
   * When the 48h dispute window closed — the moment this payment became
   * releasable, and the anchor the age backstop measures from.
   *
   * Optional and nullable on purpose: a row whose payout_due_at is null or
   * unparseable has NO known age. It is never treated as infinitely old, so a
   * data defect can never cause a release.
   */
  payout_due_at?: string | null;
}

/** Fields shared by the released and rolled-over group summaries. */
interface PayoutGroupSummary {
  payee_id: string;
  currency: string;
  /** Sum of net_to_instructor_cents for this group. */
  totalCents: number;
  /** Threshold for this group's currency. */
  minimumCents: number;
  /** Number of individual payments in the group. */
  paymentCount: number;
  /**
   * Whole days since the oldest payment in the group became releasable, or
   * null when no payment in the group has a usable payout_due_at.
   */
  oldestPaymentAgeDays: number | null;
  /**
   * Payments whose payout_due_at was null, missing or unparseable. Equal to
   * paymentCount means the whole group has unknown age — an anomaly worth
   * logging, never a reason to release.
   */
  paymentsWithUnknownAge: number;
}

export type RolledOverGroup = PayoutGroupSummary;

/** A released group, with the reason, for the payout log. */
export interface ReleasedGroup extends PayoutGroupSummary {
  reason: PayoutReleaseReason;
}

export interface PayoutGroupOptions {
  /**
   * Reference instant for every age comparison. Injected, never read from the
   * clock inside this module, so the rule stays a pure function.
   */
  now: Date;
  /** Defaults to PAYOUT_AGE_BACKSTOP_DAYS. */
  backstopDays?: number;
}

export interface PayoutGroupResult {
  /** Payments that belong to released groups — ready to transfer. */
  eligible: PaymentForPayout[];
  /** One summary per released group, carrying the reason for the log. */
  released: ReleasedGroup[];
  /** Groups that stay unpaid — balance rolls over to next Friday. */
  rolledOver: RolledOverGroup[];
}

/**
 * Age of a payment in milliseconds, or null when it has no usable
 * payout_due_at. Returns null — not 0, not Infinity — for a missing or
 * malformed timestamp.
 */
function ageInMilliseconds(payment: PaymentForPayout, now: Date): number | null {
  const dueAt = payment.payout_due_at;
  if (dueAt === null || dueAt === undefined || dueAt === "") return null;

  const parsed = Date.parse(dueAt);
  if (Number.isNaN(parsed)) return null;

  return now.getTime() - parsed;
}

/**
 * Groups releasable payments by instructor + currency, then partitions them
 * into eligible (total ≥ minimum) and rolled-over (total < minimum) sets.
 *
 * Business rules enforced:
 *   - All payments for the same instructor+currency are treated as a single
 *     aggregate balance. Either all are released or none are.
 *   - The minimum threshold is currency-specific (see MINIMUM_PAYOUT_CENTS_BY_CURRENCY).
 *   - A group below the minimum is released anyway once its oldest payment
 *     became releasable more than `backstopDays` ago. Without this, money the
 *     instructor has earned and nobody disputed is held indefinitely on a
 *     threshold that is our own policy.
 *   - The minimum takes precedence as the reported reason when a group
 *     satisfies both paths, so the log reads "released because it reached the
 *     minimum", not "released because it got old".
 *   - Rolled-over payments are NOT modified — they stay payout_status='pending'
 *     and will be re-evaluated next Friday.
 *
 * Age is measured from payout_due_at, the instant the 48h dispute window
 * closed. A payment with no usable payout_due_at contributes no age: it can
 * never trigger the backstop, and it never blocks a group whose other payments
 * are old enough.
 *
 * Pure function — no I/O, no clock read, fully testable. `now` is injected.
 *
 * @example
 *   // Instructor A: R$300 total (≥ R$250 minimum) → released, minimum_reached
 *   // Instructor B: R$240 total, oldest payment 84 days old → released, age_backstop
 *   // Instructor C: R$100 total, oldest payment 2 days old → rolled over
 *   const { eligible, released, rolledOver } =
 *     groupAndFilterReleasablePayments(payments, { now: new Date() });
 */
export function groupAndFilterReleasablePayments(
  payments: PaymentForPayout[],
  { now, backstopDays = PAYOUT_AGE_BACKSTOP_DAYS }: PayoutGroupOptions,
): PayoutGroupResult {
  // Group by payee_id + currency
  const groups = new Map<string, PaymentForPayout[]>();
  for (const payment of payments) {
    const key = `${payment.payee_id}:${payment.currency.toUpperCase()}`;
    const group = groups.get(key);
    if (group) {
      group.push(payment);
    } else {
      groups.set(key, [payment]);
    }
  }

  const eligible: PaymentForPayout[] = [];
  const released: ReleasedGroup[] = [];
  const rolledOver: RolledOverGroup[] = [];

  const backstopMilliseconds = backstopDays * MILLISECONDS_PER_DAY;

  for (const group of groups.values()) {
    const currency = group[0].currency.toUpperCase();
    const totalCents = group.reduce(
      (sum, p) => sum + p.net_to_instructor_cents,
      0,
    );
    const minimumCents = getMinimumPayoutCents(currency);

    const ages = group.map((payment) => ageInMilliseconds(payment, now));
    const knownAges = ages.filter((age): age is number => age !== null);
    // The oldest payment is the one with the largest age.
    const oldestAgeMilliseconds = knownAges.length > 0 ? Math.max(...knownAges) : null;

    const summary: PayoutGroupSummary = {
      payee_id: group[0].payee_id,
      currency,
      totalCents,
      minimumCents,
      paymentCount: group.length,
      // Compare on raw milliseconds and round only for reporting, so the
      // backstop boundary is not moved by the rounding.
      oldestPaymentAgeDays:
        oldestAgeMilliseconds === null
          ? null
          : Math.floor(oldestAgeMilliseconds / MILLISECONDS_PER_DAY),
      paymentsWithUnknownAge: ages.length - knownAges.length,
    };

    // "Older than the backstop" is strictly before the cutoff instant, so a
    // group sitting at exactly the backstop still rolls over.
    const reachedBackstop =
      oldestAgeMilliseconds !== null && oldestAgeMilliseconds > backstopMilliseconds;

    if (totalCents >= minimumCents) {
      eligible.push(...group);
      released.push({ ...summary, reason: "minimum_reached" });
    } else if (reachedBackstop) {
      eligible.push(...group);
      released.push({ ...summary, reason: "age_backstop" });
    } else {
      rolledOver.push(summary);
    }
  }

  return { eligible, released, rolledOver };
}
